import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardList, Plus, Search, Calendar, ChevronRight, Sparkles } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { WeeklyReviewDetail } from "./WeeklyReviewDetail";
import { WR_STATUSES, wrStatusBadge, weekRange, fmtDate, mondayOf, todayIso } from "@/data/weeklyReviewOptions";

export const WeeklyReviewPanel = ({ initialId, initialFilter, initialView, navKey, onClearFilter }) => {
  const { canWrite } = useUser();
  const [openId, setOpenId] = useState(initialId || null);
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState(initialFilter === "finalised" ? "Finalised" : "all");
  const [byF, setByF] = useState("all");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    const [list, cur] = await Promise.all([api.weeklyReviews(), api.weeklyReviewCurrent().catch(() => null)]);
    setRows(list); setCurrent(cur);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialId) setOpenId(initialId); }, [navKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Deep-link: filter=current -> open this week's review; filter=finalised -> open last finalised.
  useEffect(() => {
    if (!current || initialId) return;
    if (initialFilter === "current" && current.current) setOpenId(current.current.id);
    else if (initialFilter === "finalised" && current.last_finalised) setOpenId(current.last_finalised.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, navKey]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.generateWeeklyReview({ week_start: mondayOf(todayIso()) });
      toast.success("Weekly review generated from live data");
      await load(); setOpenId(r.id);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not generate review";
      toast.error(msg);
      if (e.response?.status === 409 && current?.current?.id) setOpenId(current.current.id);
    }
    setGenerating(false);
  };

  if (openId) return <WeeklyReviewDetail id={openId} onBack={() => setOpenId(null)} onChanged={load} />;

  const filtered = rows.filter((r) =>
    (statusF === "all" || r.status === statusF) &&
    (byF === "all" || r.prepared_by === byF) &&
    (!q || `${r.week_start} ${r.main_achievements} ${r.main_concerns} ${r.prepared_by}`.toLowerCase().includes(q.toLowerCase())));

  const preparers = [...new Set(rows.map((r) => r.prepared_by).filter(Boolean))];
  const currentReview = current?.current;

  return (
    <div className="space-y-5" data-testid="weekly-review-panel">
      <SectionCard testid="wr-generate-card" accent="#1D4ED8" icon={ClipboardList} title="Founders' Weekly Review"
        subtitle="A structured weekly management review built from live War Room data — captured as a permanent snapshot."
        action={canWrite && (currentReview
          ? <Button size="sm" data-testid="wr-open-current" onClick={() => setOpenId(currentReview.id)} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]">Open this week's review <ChevronRight size={15} /></Button>
          : <Button size="sm" data-testid="wr-generate" onClick={generate} disabled={generating} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]"><Plus size={15} /> {generating ? "Generating…" : "Generate this week's review"}</Button>)}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-[#EAF0FF] px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#1D4ED8]">This week (w/c {current ? current.current_week_start?.slice(5) : "—"})</p>
            <p className="mt-1 text-sm font-semibold text-[#14245F]">{currentReview ? currentReview.status : "Not generated yet"}</p>
          </div>
          <div className="rounded-xl bg-[#E6FBF8] px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#0D9488]">Last finalised</p>
            <p className="mt-1 text-sm font-semibold text-[#14245F]">{current?.last_finalised ? weekRange(current.last_finalised.week_start, current.last_finalised.week_end) : "None yet"}</p>
          </div>
          <div className="rounded-xl bg-[#FFF3E9] px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#C2410C]">Review meeting</p>
            <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-[#14245F]"><Calendar size={13} /> {current ? `${current.meeting_day}, ${current.meeting_time}` : "—"}</p>
          </div>
        </div>
      </SectionCard>

      {/* History */}
      <SectionCard testid="wr-history-card" accent="#7C3AED" icon={ClipboardList} title="Historical Reviews" subtitle={`${rows.length} review${rows.length === 1 ? "" : "s"}`}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3C4]" />
            <Input data-testid="wr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reviews…" className="h-9 border-[#E4E9F3] pl-9 text-sm" />
          </div>
          <Select value={statusF} onValueChange={setStatusF}><SelectTrigger data-testid="wr-filter-status" className="h-9 w-auto min-w-[150px] border-[#E4E9F3] text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Status: All</SelectItem>{WR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select value={byF} onValueChange={setByF}><SelectTrigger data-testid="wr-filter-by" className="h-9 w-auto min-w-[150px] border-[#E4E9F3] text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Prepared by: All</SelectItem>{preparers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState image="/commando-mascot-v2.webp" icon={Sparkles} title="No reviews yet" description="Generate your first weekly review to capture a snapshot of the whole War Room." actionLabel={canWrite && !currentReview ? "Generate review" : undefined} onAction={canWrite && !currentReview ? generate : undefined} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wr-history-table">
              <thead><tr className="border-b border-[#EEF2FB] text-left text-[11px] font-bold uppercase tracking-wide text-[#5A6B8C]">
                <th className="py-2 pr-3">Week commencing</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Prepared by</th><th className="py-2 pr-3">Readiness</th><th className="hidden py-2 pr-3 lg:table-cell">Main achievement</th><th className="py-2 pr-3">Finalised</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`wr-row-${r.id}`} role="button" tabIndex={0} aria-label={`Open weekly review: ${weekRange(r.week_start, r.week_end)}`} onClick={() => setOpenId(r.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(r.id); } }} className="cursor-pointer border-b border-[#F3F6FC] transition-colors hover:bg-[#F7F9FE] focus:outline-none focus-visible:bg-[#F0F4FF]">
                    <td className="py-2.5 pr-3 font-semibold text-[#14245F]">{weekRange(r.week_start, r.week_end)}</td>
                    <td className="py-2.5 pr-3"><StatusBadge status={wrStatusBadge(r.status)} label={r.status} /></td>
                    <td className="py-2.5 pr-3 text-[#5A6B8C]">{r.prepared_by || "—"}</td>
                    <td className="py-2.5 pr-3 font-numeric font-semibold text-[#F97316]">{r.launch_readiness ?? "—"}%</td>
                    <td className="hidden max-w-[240px] truncate py-2.5 pr-3 text-[#5A6B8C] lg:table-cell">{r.main_achievements || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs text-[#5A6B8C]">{r.finalised_at ? fmtDate(r.finalised_at) : "—"}</td>
                    <td className="py-2.5 text-[#94A3C4]"><ChevronRight size={16} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};
